import {
  Alert,
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Typography
} from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../../../hooks/use-date-formatter.tsx"
import { unwrapEnvelope } from "../../../../../utils/api/unwrap-envelope.ts"

import {
  getLeadEmailPlaceholders,
  getLeadEmailTemplates
} from "../../../api/services.ts"
import type {
  LeadEmailCampaignRequest,
  LeadEmailCampaignResult,
  LeadEmailTemplate
} from "../../../api/services.types.ts"
import type { BasePagination } from "../../../../../types/api.ts"
import { LEAD_BULK_LIMIT } from "../../../LeadsActivePage/components/leads-list-table/LeadsBulkActionBar.tsx"

const { Text } = Typography
const { TextArea } = Input

/** Shown only until the server answers with its own list, so a token added
 *  there needs no release here. */
const PLACEHOLDER_FALLBACK = [
  "customer_name",
  "first_name",
  "lead_name",
  "company_name",
  "sender_name"
]

type ComposerValues = {
  template?: string
  subject?: string
  body?: string
  save_as_template_name?: string
  scheduled_for?: Dayjs
}

interface LeadsInactiveBulkBarProps {
  selectedIds?: string[]
  leadNames?: Record<string, string>
  editAllowed?: boolean
  onClear: () => void
  /** Goes through `leads.thunks.sendLeadEmailCampaign`, which owns the
   *  envelope — the component never unwraps one itself. */
  onSend: (params: LeadEmailCampaignRequest) => Promise<LeadEmailCampaignResult>
}

export function LeadsInactiveBulkBar({
  selectedIds = [],
  leadNames = {},
  editAllowed,
  onClear,
  onSend
}: LeadsInactiveBulkBarProps) {
  const { t } = useTranslation()
  // The account's configured format, not the browser's.
  const { dateFormat } = useDateFormatter()
  const [form] = Form.useForm<ComposerValues>()
  const [composing, setComposing] = useState(false)
  const [scheduling, setScheduling] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [templates, setTemplates] = useState<LeadEmailTemplate[]>([])
  const [placeholders, setPlaceholders] =
    useState<string[]>(PLACEHOLDER_FALLBACK)
  const [result, setResult] = useState<LeadEmailCampaignResult | null>(null)
  const [requestFailed, setRequestFailed] = useState(false)

  const loadTemplates = useCallback(async () => {
    const response = await getLeadEmailTemplates()
    if (!response.ok) return
    setTemplates(
      unwrapEnvelope<BasePagination<LeadEmailTemplate>>(response.data)
        ?.results ?? []
    )
  }, [])

  useEffect(() => {
    if (!composing) return
    let cancelled = false
    void loadTemplates()
    getLeadEmailPlaceholders().then((response) => {
      if (cancelled || !response.ok) return
      const served = unwrapEnvelope<{ placeholders: string[] }>(
        response.data
      )?.placeholders
      if (served?.length) setPlaceholders(served)
    })
    return () => {
      cancelled = true
    }
  }, [composing, loadTemplates])

  const overLimit = selectedIds.length > LEAD_BULK_LIMIT

  const applyTemplate = (id?: string) => {
    const template = templates.find((candidate) => candidate.id === id)
    if (!template) return
    // Overwrites rather than fills the blanks: picking a template is asking
    // for that template's wording, and a half-replaced message is nobody's.
    form.setFieldsValue({ subject: template.subject, body: template.body })
  }

  const close = () => {
    setComposing(false)
    setScheduling(false)
    form.resetFields()
  }

  const submit = async () => {
    let values: ComposerValues
    try {
      values = await form.validateFields()
    } catch {
      // The form renders its own field errors; letting the rejection escape
      // would surface as an unhandled promise from antd's onOk.
      return
    }
    setSubmitting(true)
    try {
      const sent = await onSend({
        ids: selectedIds,
        subject: values.subject,
        body: values.body,
        ...(values.template ? { template: values.template } : {}),
        ...(values.save_as_template_name
          ? { save_as_template_name: values.save_as_template_name }
          : {}),
        ...(scheduling && values.scheduled_for
          ? // Sent without an offset on purpose: the server reads a bare
            // wall-clock time in the sender's own timezone, which is the one
            // they picked it in.
            { scheduled_for: values.scheduled_for.format("YYYY-MM-DDTHH:mm:ss") }
          : {})
      })
      setRequestFailed(false)
      setResult(sent)
      // The send happened; leaving the rows ticked invites a second campaign to
      // the same people. The bar stays up to report the outcome.
      onClear()
      if (values.save_as_template_name) void loadTemplates()
      close()
    } catch {
      setResult(null)
      setRequestFailed(true)
      close()
    } finally {
      setSubmitting(false)
    }
  }

  const clear = () => {
    setResult(null)
    setRequestFailed(false)
    onClear()
  }

  // The response's own bucket, not the recipients: deriving it from an outcome
  // would call a delivered lead "not emailed".
  const notEmailed = result?.failed ?? []

  const visible = !!selectedIds.length || !!result || requestFailed
  if (!visible) return null

  const buttonClass =
    "!h-9 !border-[#2A7D8C] !bg-[#2A7D8C] !text-white hover:!border-[#236b78] hover:!bg-[#236b78]"

  return (
    <div className="sticky bottom-0 z-30" data-testid="leads-inactive-bulk-bar">
      {selectedIds.length ? (
        <Flex
          align="center"
          gap="small"
          className="overflow-x-auto bg-[#17383E] px-4 py-3 text-white"
          role="toolbar"
          aria-label={t("leadEmail.toolbarLabel")}
          wrap={false}
        >
          <Text strong className="mr-2 whitespace-nowrap !text-white">
            {t("leadBulk.selected", { count: selectedIds.length })}
          </Text>

          {overLimit ? (
            <Text className="mr-2 whitespace-nowrap !text-amber-300">
              {t("leadEmail.overLimit", { limit: LEAD_BULK_LIMIT })}
            </Text>
          ) : null}

          {editAllowed ? (
            <Button
              className={buttonClass}
              disabled={overLimit}
              onClick={() => setComposing(true)}
            >
              {t("leadEmail.actions.sendEmail")}
            </Button>
          ) : null}

          <Button type="text" className="!text-white" onClick={clear}>
            {t("leadBulk.clearSelection")}
          </Button>
        </Flex>
      ) : null}

      {requestFailed ? (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setRequestFailed(false)}
          message={t("leadEmail.result.requestFailed")}
        />
      ) : null}

      {result ? (
        <Alert
          type="info"
          showIcon
          closable
          onClose={() => setResult(null)}
          message={t("leadEmail.result.summary", {
            queued: result.succeeded.length,
            requested: result.requested
          })}
          description={
            notEmailed.length ? (
              <ul
                data-testid="lead-email-result-exceptions"
                tabIndex={0}
                aria-label={t("leadEmail.result.exceptionsLabel", {
                  count: notEmailed.length
                })}
                className="mb-0 max-h-[30vh] list-disc overflow-y-auto pl-5"
              >
                {notEmailed.map((failure) => (
                  <li key={failure.id}>
                    {t("leadEmail.result.exception", {
                      lead: leadNames[failure.id] || failure.id,
                      reason: failure.message
                    })}
                  </li>
                ))}
              </ul>
            ) : null
          }
        />
      ) : null}

      <Modal
        open={composing}
        centered
        maskClosable={false}
        closable={!submitting}
        cancelButtonProps={{ disabled: submitting }}
        title={t("leadEmail.composer.title", { count: selectedIds.length })}
        okText={
          scheduling
            ? t("leadEmail.composer.schedule")
            : t("leadEmail.composer.sendNow")
        }
        cancelText={t("leadBulk.cancel")}
        confirmLoading={submitting}
        onOk={submit}
        onCancel={close}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false} disabled={submitting}>
          <Form.Item name="template" label={t("leadEmail.fields.template")}>
            <Select
              allowClear
              placeholder={t("leadEmail.placeholders.template")}
              onChange={applyTemplate}
              options={templates.map((template) => ({
                value: template.id,
                label: template.name
              }))}
            />
          </Form.Item>

          <Form.Item
            name="subject"
            label={t("leadEmail.fields.subject")}
            rules={[
              { required: true, message: t("leadEmail.validation.subject") }
            ]}
          >
            <Input placeholder={t("leadEmail.placeholders.subject")} />
          </Form.Item>

          <Form.Item
            name="body"
            label={t("leadEmail.fields.body")}
            rules={[{ required: true, message: t("leadEmail.validation.body") }]}
            extra={t("leadEmail.placeholderHint", {
              tokens: placeholders.map((name) => `{{${name}}}`).join(", ")
            })}
          >
            <TextArea rows={8} placeholder={t("leadEmail.placeholders.body")} />
          </Form.Item>

          <Form.Item
            name="save_as_template_name"
            label={t("leadEmail.fields.saveAsTemplate")}
          >
            <Input placeholder={t("leadEmail.placeholders.saveAsTemplate")} />
          </Form.Item>

          <Form.Item>
            <Button
              type="link"
              className="!px-0"
              onClick={() => setScheduling((current) => !current)}
            >
              {scheduling
                ? t("leadEmail.composer.sendNowInstead")
                : t("leadEmail.composer.scheduleInstead")}
            </Button>
          </Form.Item>

          {scheduling ? (
            <Form.Item
              name="scheduled_for"
              label={t("leadEmail.fields.scheduledFor")}
              rules={[
                {
                  required: true,
                  message: t("leadEmail.validation.scheduledFor")
                },
                {
                  // Also refused server-side; this only saves a round trip.
                  validator: (_rule, value?: Dayjs) =>
                    !value || value.isAfter(dayjs())
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(t("leadEmail.validation.scheduledInPast"))
                        )
                }
              ]}
            >
              <DatePicker
                showTime={{ format: "HH:mm" }}
                format={`${dateFormat} HH:mm`}
                className="w-full"
              />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>
    </div>
  )
}
